import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@core/auth';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@shared/services';
import { TypedRoute } from '@shared/utils';
import { switchMap } from 'rxjs';

@Component({
  selector: 'app-activate',
  templateUrl: './activate.component.html',
  styleUrl: './activate.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivateComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  constructor(
    @Inject(ActivatedRoute) private readonly route: TypedRoute<{ token: string }>,
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
  ) {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      const token = data.token;
      if (!token) {
        this.redirectToLogin();
        return;
      }
      this.authService
        .logOutWithoutRedirect()
        .pipe(switchMap(() => this.authService.activateAccount(token)))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            const redirectPath = this.route.snapshot.queryParamMap.get('redirect');

            if (response.meta?.session_token) {
              this.authService.logIn(response.meta.session_token, redirectPath ?? undefined);
            } else {
              this.showUnknownErrorToast();
              this.redirectToLogin();
            }
          },
          error: (error: HttpErrorResponse) => {
            this.authService.showTokenVerifyErrorToast(error);
            this.redirectToLogin();
          },
        });
    });
  }

  private redirectToLogin(): void {
    this.router.navigate(['/login']);
  }

  private showUnknownErrorToast(): void {
    this.toastService.add({
      message: this.translateService.instant('Common.Errors.UnknownError'),
      type: 'error',
    });
  }
}
